// app/src/pages/CalendarPage.tsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "../api/Client";
import { SecureStorage } from "../storage/SecureStorage";
import { ModalHeader } from "../components/Header";
import { useSileo } from "../components/Sileo";
import { shareICS } from "../ICS/filesystem";
import { cancelBookingReminder, scheduleManyBookingReminders } from "../notifications/booking";
import { useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";

/* =================== Tipos =================== */
type ActiveBooking = {
  booking_id: number;
  coach_name: string;
  product_name?: string;
  service_name?: string;
  payment_status?: string;
  start_date: string;   // ISO yyyy-mm-dd...
  start_time?: string;  // HH:mm:ss
  status: "active" | "cancelled" | string;
  is_customer_late?: number;
  is_customer_absent?: number;
};

type CustomerGetResponse = {
  data: {
    active_bookings: ActiveBooking[];
    coach?: number | string | null; // favorito
  };
};

// /api/customers/reservations/user-bookings (id == booking_id)
type UserBooking = {
  id: number;                 // booking_id
  date: string;               // yyyy-mm-dd
  hour: string;               // HH:mm:ss
  service_id: number;
  service?: string | null;
  coach_name?: string | null;
  coach_profile_pic?: string | null; // URL
  product_id?: number | null;
  active_product_id?: number | null;
};

type ServiceItem = { service_id: number; service_name: string; centro?: string | null };
type PrimaryServiceItem = { primary_service_id: number; name: string };
type WeeklyHour = { service_id: number; date: string; hour: string; booked: number; capacity: number; duration?: number };
type HourlyCoach = { service_id: number; date: string; hour: string; coach_id: number; coach_name: string; booked: number; capacity: number };
type TimeslotOption = { id: number; name: string };

type WeeklyLimitEntry = {
  active_product_id: number | null;
  product_id: number;
  type_of_product: string; // "recurrent" o bono
  weekly_limit: number;
  used: number;
  remaining: number;
  total_limit?: number | null;
};

type UserProduct = {
  id: number;
  name: string;
  price?: number | null;
  serviceIds: number[];
  // Campo informativo; el crédito real lo calculamos con /user-weekly-limit
  sessionsRemaining: number | null;
  centro?: string | null;
  active_product_id?: number | null; // Added property
  subscription_status?: string | null;
  expiry_date?: string | null; // YYYY-MM-DDTHH:mm:ss
  type_of_product?: string | null;
  payment_status?: string | null;
  purchase_date?: string | null;
};

type PastSessionDetail = {
  booking_id: number;
  product_name?: string;
  service_name?: string;
  coach_name?: string;
  date: string;      // YYYY-MM-DD
  time?: string;     // HH:mm
  comment?: string | null;
  payment_status?: string;
  is_customer_late: number;
  is_customer_absent: number;
};

type PreferredCoachItem = { coach_id: number; priority?: number | null };

/* =================== Utils =================== */
const weekdaysShort = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const ymd = (d: Date) => { const x = new Date(d.getTime()); x.setHours(12, 0, 0, 0); return x.toISOString().slice(0, 10); };
const parseYMD = (s: string) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, (m || 1) - 1, (d || 1)); };
const apiYMD = (input?: string | null) => (input ? input.slice(0, 10) : "");
const fmtDateLong = (s: string) => new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "long", year: "numeric" }).format(parseYMD(s));
const paymentStatusLabel = (status?: string | null) => {
  if (!status) return null;
  const normalized = status.toLowerCase();
  if (normalized === "paid" || normalized === "pagado") return "Pagado";
  if (normalized === "pending" || normalized === "pendiente") return "Pendiente";
  if (normalized === "cancelled" || normalized === "cancelado") return "Cancelado";
  return capitalize(status);
};

function initialsFromName(name?: string | null) {
  if (!name) return "–";
  const parts = name.trim().split(/\s+/);
  const f = parts[0]?.[0] ?? "";
  const l = (parts.length > 1 ? parts[parts.length - 1][0] : parts[0]?.[0]) ?? "";
  const res = (f + l).toUpperCase();
  return res || "–";
}

function normalizePhotoUrl(url?: string | null): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return url;
  return `/${url}`;
}

/* =================== Componente =================== */
export default function CalendarPage() {
  const today = new Date();
  const todayStr = ymd(today);
  const navigate = useNavigate();
  const sileo = useSileo();
  const location = useLocation() as { state?: { productId?: number; date?: string , activeProductId?: number } };
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

  // === Cache en memoria (no persistente) ===
  const cacheRef = useRef<{
    weeklyByKey: Map<string, WeeklyHour[]>;          // key: `${serviceId}|${dateYmd}`
    weeklyPromiseByKey: Map<string, PromiseLike<WeeklyHour[]>>;
    hourlyByKey: Map<string, HourlyCoach[]>;         // key: `${serviceId}|${date}|${timeHHMM}`
    weeklyLimitsByDate: Map<string, WeeklyLimitEntry[]>; // key: `${userId}|${targetDate}`
    favoritesByKey: Map<string, number | null>;      // key: `${userId}|${primaryServiceName}`
    timeslotByHHMM: Map<string, number>;             // keys: `${sessionName}|HH:MM` and `__global__|HH:MM`
    timeslotByService: Map<number, Map<string, number>>; // serviceId -> (HH:MM -> session_timeslot_id)
    holidaysFetched: boolean;
    bookingsFetchedAt: number;
  }>(
    {
      weeklyByKey: new Map(),
      weeklyPromiseByKey: new Map(),
      hourlyByKey: new Map(),
      weeklyLimitsByDate: new Map(),
      favoritesByKey: new Map(),
      timeslotByHHMM: new Map(),
      timeslotByService: new Map(),
      holidaysFetched: false,
      bookingsFetchedAt: 0,
    }
  );

  // Mes / selección
  const [currentYear, setYear] = useState<number>(today.getFullYear());
  const [currentMonth, setMonth] = useState<number>(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [activeHour, setActiveHour] = useState<string>("");

  // Usuario (para evitar leer SecureStorage muchas veces)
  const [userId, setUserId] = useState<number | null>(null);

  // VIP
  const [isVip, setIsVip] = useState<boolean>(false);

  // Datos
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [bookings, setBookings] = useState<ActiveBooking[]>([]);
  const [userBookings, setUserBookings] = useState<UserBooking[]>([]);
  const [services, setServices] = useState<ServiceItem[]>([]);
  // primaryServices is provided by API but not used directly here; keep in cacheRef if needed.
  const [timeslots, setTimeslots] = useState<TimeslotOption[]>([]);
  const [weeklyLimits, setWeeklyLimits] = useState<WeeklyLimitEntry[]>([]);

  const [favoriteByService, setFavoriteByService] = useState<Record<number, number | null>>({});
  const [preferredCoaches, setPreferredCoaches] = useState<PreferredCoachItem[]>([]);
  const [userProducts, setUserProducts] = useState<UserProduct[]>([]);

  // Pestaña activa para reservas vs historial
  const [sessionsTab, setSessionsTab] = useState<"upcoming" | "past">("upcoming");
  const [recoverMode, setRecoverMode] = useState<null | number>(null);

  // Festivos
  const [holidaySet, setHolidaySet] = useState<Set<string>>(new Set());

  // Modal reservar
  const [showModal, setShowModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | "">("");
  const [selectedServiceId, setSelectedServiceId] = useState<number | "">("");
  const [timeTab, setTimeTab] = useState<"am" | "pm">("am");

  // Reprogramación
  const [rescheduleFrom, setRescheduleFrom] = useState<ActiveBooking | null>(null);

  // Horas
  const [dayHours, setDayHours] = useState<WeeklyHour[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [hourDetail, setHourDetail] = useState<{
    hour: string;
    loading: boolean;
    error: string | null;
    coaches: HourlyCoach[];
    chosenCoachId: number | null;
    manualCoachId: number | null;
  }>({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null, manualCoachId: null });

  const [antelacion, setAntelacion] = useState<{
    horas_antelacion_reserva: number;
    horas_antelacion_cambio: number;
    dias_antelacion_reserva: number;
  } | null>(null);

  // Cancelación
  const [cancelBusyId, setCancelBusyId] = useState<number | null>(null);

  // Intercambio (sin sesiones)
  const [swapCandidateId, setSwapCandidateId] = useState<number | "">("");
  const [swapCandidateDate, setSwapCandidateDate] = useState<Date | null>(null);
  const [swapBusy, setSwapBusy] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);

  // Historial pasado (modal)
  const [showPastModal, setShowPastModal] = useState(false);
  const [pastLoading, setPastLoading] = useState(false);
  const [pastError, setPastError] = useState<string | null>(null);
  const [pastSessions, setPastSessions] = useState<PastSessionDetail[]>([]);

  // Fotos (para lista de reservas)
  const [coachPhotoByBooking, setCoachPhotoByBooking] = useState<Record<number, string | null>>({});
  const [cuestionarioActivo, setCuestionarioActivo] = useState(false);
  // Primary services from API (we only need the setter in this file)
  const [, setPrimaryServices] = useState<PrimaryServiceItem[]>([]);

  // Swap mode and questionnaire state (only setters are used)
  const [, setSwapMode] = useState(false);
  const [, setPreguntaActual] = useState(0);
  const [, setRespuestas] = useState<string[]>([]);
  const [bookingIdPendiente, setBookingIdPendiente] = useState<number | null>(null);

  const [bookingBusy, setBookingBusy] = useState(false);

  /* ====== Cuestionario pre-sesión ====== */
  useEffect(() => {
    async function checkUpcomingSession() {
      try {
        if (!userId) return;
        const now = new Date();
        const sesionProxima = userBookings.find(s => {
          const fechaSolo = s.date.slice(0, 10);
          const hora = s.hour.length === 5 ? `${s.hour}:00` : s.hour;
          const start = new Date(`${fechaSolo}T${hora}`);
          const diffMinutes = (start.getTime() - now.getTime()) / 60000;
          return diffMinutes >= 0 && diffMinutes <= 60; // ≤ 1h
        });

        if (sesionProxima) {
          try {
            const r = await api.get(`/api/customers/reservations/booking-questionnaire/${sesionProxima.id}`);
            if (r.status === 404 || r.data === false) {
              setBookingIdPendiente(sesionProxima.id);
              setCuestionarioActivo(true);
              setPreguntaActual(0);
              setRespuestas([]);
            }
          } catch (err: any) {
            if (err?.response?.status === 404) {
              setBookingIdPendiente(sesionProxima.id);
              setCuestionarioActivo(true);
              setPreguntaActual(0);
              setRespuestas([]);
            }
          }
        }
      } catch (err) {
        console.error("❌ Error comprobando cuestionario:", err);
      }
    }
    if (userBookings.length > 0) checkUpcomingSession();
  }, [userBookings, userId]);

  async function responderPregunta(respuesta: string) {
    await enviarRespuestas([respuesta]);
  }

  async function enviarRespuestas(respuestasFinal: string[]) {
    if (!bookingIdPendiente) return;
    try {
      await api.post("/api/customers/reservations/booking-questionnaire", {
        booking_id: bookingIdPendiente,
        estado: respuestasFinal[0]   // 👈 solo un campo
      });
      setCuestionarioActivo(false);
    } catch (err) {
      console.error("❌ Error al enviar cuestionario:", err);
    }
  }

  function omitirFormulario() {
    setCuestionarioActivo(false); // no persistimos → se repetirá en el próximo login
  }


  /* ====== Carga inicial (solo una vez) ====== */
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const user = await SecureStorage.getUser();
        if (!user?.id) throw new Error("Usuario no disponible. Vuelve a iniciar sesión.");
        if (!alive) return;
        setUserId(user.id);

        // VIP + datos base en paralelo
        const [
          rVip,
          rCustomer,
          rUserBookings,
          rPrimaryServices,
          rServices,
          rTimeslots,
          rUserProducts,
          rPreferredCoaches,
        ] = await Promise.allSettled([
          api.get(`/api/customers/vip/status/${user.id}`),
          api.get(`/api/customers/get/${user.id}`),
          api.get(`/api/customers/reservations/user-bookings`, { params: { user_id: user.id } }),
          api.get(`/api/customers/setup/primary-services`),
          api.get(`/api/customers/setup/services`),
          api.get(`/api/customers/setup/session-timeslots`),
          api.get(`/api/customers/catalog/user-products`),
          api.get(`/api/customers/reservations/preferred-coach`, { params: { customer_id: user.id } }),
        ]);

        // VIP
        if (rVip.status === "fulfilled") {
          const { is_vip } = (rVip.value.data as { is_vip: boolean });
          setIsVip(!!is_vip);
        } else {
          setIsVip(false);
        }

        // Bookings + cache de foto
        if (rCustomer.status === "fulfilled") {
          const cjson: CustomerGetResponse = rCustomer.value.data as CustomerGetResponse;
          setBookings(Array.isArray(cjson.data?.active_bookings) ? cjson.data.active_bookings : []);
          cacheRef.current.bookingsFetchedAt = Date.now();
        }
        if (rUserBookings.status === "fulfilled") {
          const ubs: UserBooking[] = Array.isArray(rUserBookings.value.data) ? rUserBookings.value.data : [];
          setUserBookings(ubs);
          const photoMap: Record<number, string | null> = {};
          for (const ub of ubs) photoMap[ub.id] = normalizePhotoUrl(ub.coach_profile_pic);
          setCoachPhotoByBooking(photoMap);
          const isWeb = Capacitor.getPlatform() === "web";
          if (!isWeb) {
            try {
              const bookingRefs = ubs
                .filter(b => b?.id && b?.date && b?.hour)
                .map((b) => {
                  const hour = String(b.hour ?? "");
                  const hhmmss = hour.length === 5 ? `${hour}:00` : hour.slice(0, 8) || "00:00:00";
                  return {
                    bookingId: Number(b.id),
                    serviceName: b.service ?? undefined,
                    coachName: b.coach_name ?? undefined,
                    startISO: `${String(b.date).slice(0, 10)}T${hhmmss}`,
                  };
                })
                .filter(b => Number.isFinite(b.bookingId));
              if (bookingRefs.length) {
                await scheduleManyBookingReminders(bookingRefs);
                console.log(`Recordatorios programados para ${bookingRefs.length} sesiones`);
              }
            } catch (err) {
              console.warn("Error programando recordatorios locales:", err);
            }
          }
        }

        // Servicios / primarios
        if (rServices.status === "fulfilled") {
          const servicesArr: ServiceItem[] = Array.isArray(rServices.value.data) ? rServices.value.data : [];
          setServices(servicesArr);
        }
        if (rPrimaryServices.status === "fulfilled") {
          const primaryArr: PrimaryServiceItem[] = Array.isArray(rPrimaryServices.value.data) ? rPrimaryServices.value.data : [];
          setPrimaryServices(primaryArr);
        }

            // Timeslots → lista + mapa O(1)
            if (rTimeslots.status === "fulfilled") {
              const raw: any[] = Array.isArray(rTimeslots.value.data) ? rTimeslots.value.data : [];
              // keep a simple timeslots array for UI if needed
              const ts: TimeslotOption[] = raw.map((t: any) => ({ id: t.session_timeslot_id, name: t.timeslot }));
              setTimeslots(ts);
              const map = cacheRef.current.timeslotByHHMM;
              map.clear();
              // Key by `${session_name}|HH:MM` and also keep a global fallback `__global__|HH:MM`
              raw.forEach(r => {
                const hhmm = String(r.timeslot).slice(0, 5);
                const sessionName = String(r.session_name || "");
                if (sessionName) map.set(`${sessionName}|${hhmm}`, r.session_timeslot_id);
                const globalKey = `__global__|${hhmm}`;
                if (!map.has(globalKey)) map.set(globalKey, r.session_timeslot_id);
              });
            }

        // Productos de usuario
        if (rUserProducts.status === "fulfilled") {
          const normalizedProducts = normalizeUserProducts(rUserProducts.value.data);
          setUserProducts(normalizedProducts);
        }

        if (rPreferredCoaches.status === "fulfilled") {
          const normalizedPreferred = normalizePreferredCoaches((rPreferredCoaches.value.data as any)?.preferred_coaches);
          setPreferredCoaches(normalizedPreferred);
        } else {
          setPreferredCoaches([]);
        }

        // Favoritos por servicio (en paralelo + cache local por nombre)
        if (rPrimaryServices.status === "fulfilled" && rPrimaryServices.value.data && Array.isArray(rPrimaryServices.value.data)) {
          const primaryArr: PrimaryServiceItem[] = rPrimaryServices.value.data;
          const favPromises = primaryArr.map(async (svc) => {
            const key = `${user.id}|${svc.name}`;
            if (cacheRef.current.favoritesByKey.has(key)) {
              return [svc.primary_service_id, cacheRef.current.favoritesByKey.get(key) ?? null] as const;
            }
            try {
              const r = await api.get("/api/customers/reservations/preferred-coach-with-service", {
                params: { customer_id: user.id, service_name: svc.name }
              });
              const pref = (r.data as { preferred_coach_id?: number | null })?.preferred_coach_id ?? null;
              cacheRef.current.favoritesByKey.set(key, pref);
              return [svc.primary_service_id, pref] as const;
            } catch (err: any) {
              if (err?.response?.status !== 404) console.error("❌ Error cargando favorito:", err);
              cacheRef.current.favoritesByKey.set(key, null);
              return [svc.primary_service_id, null] as const;
            }
          });

          const favPairs = await Promise.all(favPromises);
          const favs: Record<number, number | null> = {};
          for (const [psid, pref] of favPairs) favs[psid] = pref;
          setFavoriteByService(favs);
        }

        // ❌ OJO: Ya no se cargan los weekly limits aquí; se hace en un efecto separado.

      } catch (e: any) {
        if (e?.response?.status !== 404) setError(e.message || "Error al cargar datos");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, []);

  // Abrir modal directo con producto (desde navegación con state)
  useEffect(() => {
    // Support both { productId } (legacy) and { activeProductId } (preferred when available)
    const pid = location?.state?.productId;
    const apid = (location as any)?.state?.activeProductId;
    const incoming = apid ?? pid;
    if (!incoming) return;

    // If incoming is an active_product_id, use it directly. Otherwise try to
    // resolve to the active_product_id for the product, falling back to the pid.
    const asActive = userProducts.find(p => p.active_product_id === incoming || p.id === incoming);
    if (asActive) setSelectedProductId(asActive.active_product_id ?? asActive.id);
    else setSelectedProductId(incoming);

    setShowModal(true);
    // Clear navigation state so it doesn't re-trigger when using back/replace
    navigate(".", { replace: true, state: {} });
  }, [location?.state, navigate, userProducts]);

  /* Festivos — una sola llamada */
  useEffect(() => {
    (async () => {
      try {
        if (cacheRef.current.holidaysFetched) return;
        const r = await api.get(`/api/customers/reservations/holidays`);
        const dates = Array.isArray(r.data) ? r.data : [];
        const set = new Set<string>(dates.map((d: any) => apiYMD(d?.date ?? d)).filter(Boolean));
        setHolidaySet(set);
        cacheRef.current.holidaysFetched = true;
      } catch (err: any) {
        if (err?.response?.status === 404) return; // sin festivos
        console.error("❌ Error cargando festivos:", err);
      }
    })();
  }, []);

  /* Refrescar reservas al cambiar de mes — throttle (evita golpear server por cada click) */
  useEffect(() => {
    (async () => {
      try {
        const now = Date.now();
        if (!userId) return;
        // 5 minutos de frescura
        const STALE_MS = 5 * 60 * 1000;
        if (now - cacheRef.current.bookingsFetchedAt < STALE_MS) return;

        const r = await api.get(`/api/customers/get/${userId}`);
        const cjson: CustomerGetResponse = r.data as CustomerGetResponse;
        setBookings(Array.isArray(cjson.data?.active_bookings) ? cjson.data.active_bookings : []);
        cacheRef.current.bookingsFetchedAt = now;
      } catch (e) {
        // silencioso
      }
    })();
  }, [currentMonth, currentYear, userId]);

  /* Al cambiar producto: seleccionar automáticamente servicio único y cargar horas (usando cache) */
  const servicesForProduct = useMemo<ServiceItem[]>(() => {
    if (!selectedProductId) return [];
    const sel = userProducts.find(p => p.id === selectedProductId || p.active_product_id === selectedProductId);
    if (!sel) return [];

    const allowedServiceIds = sel.serviceIds ?? [];
    if (!allowedServiceIds.length) return [];

    let filtered = services.filter(s => {
      const matchesProduct = allowedServiceIds.includes(Number(s.service_id));
      if (!matchesProduct) return false;

      // Si el producto es VIP, puede acceder a todos los servicios
      if (sel?.centro?.toLowerCase() === "vip") return true;
      
      // Si el SERVICIO es VIP, está disponible para todos los centros
      if (s.centro?.toLowerCase() === "vip") return true;
      
      // Si el producto tiene centro específico, debe coincidir con el servicio
      if (sel?.centro) {
        return (s.centro?.toLowerCase() || "") === sel.centro.toLowerCase();
      }
      return true;
    });




    // Excepción de “Sotillo sábado → incluir 2 Guadarrama”
    if (
      selectedProductId === 41 &&
      selectedProduct?.centro?.toLowerCase() === "sotillo" &&
      (() => {
        if (!selectedDate) return false;
        const d = selectedDate instanceof Date ? selectedDate : parseYMD(String(selectedDate));
        return d.getDay() === 6; // sábado
      })()
    ) {
      const exists = filtered.some(s => s.service_id === 2);
      if (!exists) {
        const guadarrama =
          services.find(s => s.service_id === 2 && (s.service_name || "").toUpperCase().includes("GUADARRAMA")) ||
          { service_id: 2, service_name: "ENTRENAMIENTO GUADARRAMA", centro: "guadarrama" };
        filtered = [...filtered, guadarrama];
      }
    }

    return filtered;
  }, [selectedProductId, services, userProducts, selectedDate]);

  const bookingMetaById = useMemo(() => {
    const map = new Map<number, { product_id: number | null; active_product_id: number | null; service_id: number | null }>();
    for (const ub of userBookings) {
      map.set(ub.id, {
        product_id: ub.product_id ?? null,
        active_product_id: ub.active_product_id ?? null,
        service_id: ub.service_id ?? null,
      });
    }
    return map;
  }, [userBookings]);

  useEffect(() => {
    (async () => {
      if (!selectedProductId) {
        setSelectedServiceId("");
        setAntelacion(null);
        // no vaciamos weeklyLimits aquí: se comparten entre productos en esa fecha
        return;
      }

      if (rescheduleFrom) {
        try {
          const rAnt = await api.get(`/api/customers/setup/product-from-active-product/${selectedActiveProductId}`);
          if (rAnt.data && typeof rAnt.data === "object") {
            const antData = rAnt.data as {
              horas_antelacion_reserva?: number | string;
              horas_antelacion_cambio?: number | string;
              dias_antelacion_reserva?: number | string;
            };
            setAntelacion({
              horas_antelacion_reserva: Number(antData.horas_antelacion_reserva) || 0,
              horas_antelacion_cambio: Number(antData.horas_antelacion_cambio) || 0,
              dias_antelacion_reserva: Number(antData.dias_antelacion_reserva) || 0,
            });
          } else {
            setAntelacion(null);
          }
        } catch {
          setAntelacion(null);
        }

        // Si ya hay servicio fijado, precargar horas del día seleccionado
        if (selectedServiceId) {
          await fetchWeeklyForDay(Number(selectedServiceId), ymd(selectedDate));
        }
        // No seguir con la lógica de auto-selección/limpieza
        return;
      }
      // Si solo hay un servicio, auto-seleccionarlo y precargar horas
      if (servicesForProduct.length === 1) {
        const onlyService = servicesForProduct[0];
        if (selectedServiceId !== onlyService.service_id) {
          setSelectedServiceId(onlyService.service_id);
          setActiveHour("");
          await fetchWeeklyForDay(onlyService.service_id, ymd(selectedDate)); // cacheado
        }
      } else if (!servicesForProduct.find(s => s.service_id === selectedServiceId)) {
        setSelectedServiceId("");
        setDayHours([]);
        setActiveHour("");
      }

      // Antelación del producto
      try {
        const rAnt = await api.get(`/api/customers/setup/product-from-active-product/${selectedActiveProductId}`);
        if (rAnt.data && typeof rAnt.data === "object") {
          const antData = rAnt.data as {
            horas_antelacion_reserva?: number | string;
            horas_antelacion_cambio?: number | string;
            dias_antelacion_reserva?: number | string;
          };
          setAntelacion({
            horas_antelacion_reserva: Number(antData.horas_antelacion_reserva) || 0,
            horas_antelacion_cambio: Number(antData.horas_antelacion_cambio) || 0,
            dias_antelacion_reserva: Number(antData.dias_antelacion_reserva) || 0,
          });
        } else {
          setAntelacion(null);
        }
      } catch (err) {
        console.error("❌ Error cargando antelación:", err);
        setAntelacion(null);
      }

      // ⛔️ Eliminado: el fetch de weekly limits estaba duplicado aquí.

    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, servicesForProduct, selectedServiceId, selectedDate, userId, rescheduleFrom]);

  // Weekly limit — depende solo de la fecha seleccionada (y userId)
  useEffect(() => {
    (async () => {
      try {
        if (!userId) return;
        const key = `${userId}|${ymd(selectedDate)}`;
        if (cacheRef.current.weeklyLimitsByDate.has(key)) {
          setWeeklyLimits(cacheRef.current.weeklyLimitsByDate.get(key)!);
        } else {
          const rWeekly = await api.get(`/api/customers/reservations/weekly-limit`, {
            params: { user_id: userId, target_date: ymd(selectedDate) },
          });
          const weeklyData = (rWeekly.data as { weekly_limits?: WeeklyLimitEntry[] })?.weekly_limits ?? [];
          cacheRef.current.weeklyLimitsByDate.set(key, weeklyData);
          setWeeklyLimits(weeklyData);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) console.error("❌ Error cargando límites:", err);
        setWeeklyLimits([]);
      }
    })();
  }, [selectedDate, userId]);

  // Auto-mapa HH:MM -> timeslot_id cuando cambian timeslots (seguridad extra)
  useEffect(() => {
    const map = cacheRef.current.timeslotByHHMM;
    map.clear();
    timeslots.forEach(t => map.set(String(t.name).slice(0, 5), t.id));
  }, [timeslots]);

  // Cuando cambia el servicio seleccionado, cargar timeslots específicos de ese servicio
  useEffect(() => {
    async function loadForService() {
      if (!selectedServiceId) return;
      const sid = Number(selectedServiceId);
      if (cacheRef.current.timeslotByService.has(sid)) return;
      try {
        const r = await api.get(`/api/customers/setup/session-timeslots/service/${sid}`);
        const rows: any[] = Array.isArray(r.data) ? r.data : [];
        const svcMap = new Map<string, number>();
        rows.forEach(rr => {
          const hh = String(rr.timeslot).slice(0, 5);
          svcMap.set(hh, rr.session_timeslot_id);
          // also ensure global fallback map contains at least one id for hh
          const globalKey = `__global__|${hh}`;
          if (!cacheRef.current.timeslotByHHMM.has(globalKey)) cacheRef.current.timeslotByHHMM.set(globalKey, rr.session_timeslot_id);
        });
        cacheRef.current.timeslotByService.set(sid, svcMap);
      } catch (err: any) {
        console.warn("No se pudieron cargar timeslots por servicio:", err?.message || err);
      }
    }
    loadForService();
  }, [selectedServiceId]);

  /* =================== Cálculos =================== */
  const datesWithSessions = useMemo(() => {
    const set = new Set<string>();
    bookings.forEach(b => { if (b.status === "active") { const d = apiYMD(b.start_date); if (d) set.add(d); } });
    return set;
  }, [bookings]);

  const allReservedSorted = useMemo(() => {
    const toIso = (b: ActiveBooking) => `${apiYMD(b.start_date)}T${(b.start_time || "00:00:00").slice(0, 8)}`;
    return bookings
      .filter(b => b.status === "active")
      .slice()
      .sort((a, b) => toIso(a).localeCompare(toIso(b)));
  }, [bookings]);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat("es-ES", { month: "long", year: "numeric" }).format(new Date(currentYear, currentMonth, 1)),
    [currentYear, currentMonth]
  );

  const firstWeekdayOffset = (y: number, m: number) => (new Date(y, m, 1).getDay() + 6) % 7; // Lunes=0
  const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
  const goPrevMonth = useCallback(() => { const d = new Date(currentYear, currentMonth - 1, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); }, [currentYear, currentMonth]);
  const goNextMonth = useCallback(() => { const d = new Date(currentYear, currentMonth + 1, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); }, [currentYear, currentMonth]);

  const selectedActiveProductId = useMemo(() => {
  const found = userProducts.find(p => p.id === selectedProductId || p.active_product_id === selectedProductId);
    return found?.active_product_id ?? null;
  }, [selectedProductId, userProducts]);


  /* Crédito restante real usando /user-weekly-limit */
  // Determine the weekly-limit entry that corresponds to the currently selected product.
  // Because the backend can return multiple entries with the same product_id but
  // different active_product_id, we try several fallbacks to find the correct one:
  // 1) match by active_product_id === selectedProductId
  // 2) match by active_product_id === selectedActiveProductId (if we derived it)
  // 3) match by product_id === selectedProductId
  // The resulting `serviceCredit` is built from that chosen weeklyLimits entry.
  const serviceCredit = useMemo(() => {
    if (!selectedProductId || weeklyLimits.length === 0) return null;

    // try direct match: maybe selectedProductId already is an active_product_id
    let entry = weeklyLimits.find(w => w.active_product_id === selectedProductId);

    // fallback: if we derived an active id from userProducts, try that
    if (!entry && selectedActiveProductId != null) {
      entry = weeklyLimits.find(w => w.active_product_id === selectedActiveProductId);
    }

    // final fallback: match by base product_id (less specific)
    if (!entry) {
      entry = weeklyLimits.find(w => w.product_id === selectedProductId);
    }

    if (!entry) return null;

    if (entry.type_of_product === "recurrent") {
      return {
        mode: "weekly" as const,
        type: entry.type_of_product,
        remaining: entry.remaining,
        limit: entry.weekly_limit ?? 0,
        used: entry.used,
        _matchedEntry: entry, // attach for other logic to reuse
      } as any;
    } else {
      return {
        mode: "total" as const,
        type: entry.type_of_product,
        remaining: entry.remaining,
        limit: entry.total_limit ?? 0,
        used: entry.used,
        _matchedEntry: entry,
      } as any;
    }
  }, [selectedProductId, selectedActiveProductId, weeklyLimits]);

  /* Candidatas para intercambio según el modo de crédito */
  const swapCandidates = useMemo(() => {
    if (!serviceCredit || !selectedProductId) return [];
    const todayIso = todayStr;

    const toLabel = (b: ActiveBooking) =>
      `${apiYMD(b.start_date)} · ${(b.start_time || "").slice(0, 5)} — ${b.service_name} (${b.coach_name || "Coach"})`;

    const notTooSoon = (b: ActiveBooking) =>
      !!b.start_time && hoursUntil(apiYMD(b.start_date), b.start_time) >= 12;

    const sameProduct = (b: ActiveBooking) => {
      const meta = bookingMetaById.get(b.booking_id);
      if (!meta) return false;
      const metaIds = [meta.product_id, meta.active_product_id].filter((id): id is number => id != null);
      if (metaIds.length === 0) return false;
      // If we found a matched weeklyLimits entry, compare against both its
      // product_id and active_product_id. Otherwise, try both comparisons
      // with selectedProductId/selectedActiveProductId as a last resort.
      const matched = (serviceCredit as any)?._matchedEntry as WeeklyLimitEntry | undefined;
      if (matched) {
        return metaIds.some(id => id === matched.product_id || id === matched.active_product_id);
      }
      if (selectedActiveProductId != null) {
        return metaIds.some(id => id === selectedActiveProductId || id === selectedProductId);
      }
      return metaIds.some(id => id === selectedProductId);
    };

    return allReservedSorted
      .filter((b) => apiYMD(b.start_date) >= todayIso)
      .filter(notTooSoon)
      .filter(sameProduct)
      .map((b) => ({ id: b.booking_id, label: toLabel(b), start_date: b.start_date }));
  }, [serviceCredit, allReservedSorted, todayStr, selectedProductId, selectedActiveProductId, bookingMetaById]);


  // NOTE: missedSessions removed (unused). Recreate from weeklyLimits if needed.

  /* ====== Acciones ====== */
  const onDayClick = useCallback(async (dateStr?: string, disabled?: boolean) => {
    if (!dateStr || disabled) return;

    const isPast = dateStr < todayStr;
    const hadSessions = datesWithSessions.has(dateStr);

    setSelectedDate(parseYMD(dateStr));
    setActiveHour(""); // evita que quede marcada una hora de otro día

    // Histórico en días pasados
    if (isPast && hadSessions) {
      setShowPastModal(true);
      setPastLoading(true);
      setPastError(null);
      try {
        const sameDay = bookings
          .filter(b => apiYMD(b.start_date) === dateStr && b.status === "active");
        const details: PastSessionDetail[] = sameDay.map(b => ({
          booking_id: b.booking_id,
          product_name: b.product_name,
          service_name: b.service_name,
          coach_name: b.coach_name,
          date: dateStr,
          time: b.start_time ? b.start_time.slice(0, 5) : undefined,
          payment_status: b.payment_status,
          comment: null,
          is_customer_late: Number(b.is_customer_late ?? 0),
          is_customer_absent: Number(b.is_customer_absent ?? 0),
        }));
        setPastSessions(details);
      } catch (e: any) {
        setPastError(e.message || "No se pudo cargar el historial del día");
      } finally {
        setPastLoading(false);
      }
      return;
    }

    // Modal de reserva (hoy/futuro)
    setShowModal(true);
    setRescheduleFrom(null);
    setSwapMode(false);
    setSwapCandidateId("");
    setSwapError(null);

    setSelectedProductId("");
    setSelectedServiceId("");
    setDayHours([]);
    setHourDetail({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null, manualCoachId: null });
  }, [todayStr, datesWithSessions, bookings]);

  // === PRODUCTOS y SERVICIOS por producto ===
  // Resolve selected product by product `id` or its `active_product_id`.
  const selectedProduct = useMemo(() => {
    if (!selectedProductId) return null;
    return userProducts.find(p => p.id === selectedProductId || p.active_product_id === selectedProductId) || null;
  }, [selectedProductId, userProducts]);

  const pendingPaymentRestriction = useMemo(() => {
    if (!selectedProduct) return null;
    const status = String(selectedProduct.payment_status ?? "").toLowerCase();
    if (status !== "pending" && status !== "pendiente") return null;
    const purchaseYmd = apiYMD(selectedProduct.purchase_date);
    if (!purchaseYmd) return null;
    const purchaseDate = parseYMD(purchaseYmd);
    purchaseDate.setDate(purchaseDate.getDate());
    return {
      purchaseYmd,
      minSelectableYmd: ymd(purchaseDate),
    };
  }, [selectedProduct]);

  useEffect(() => {
    if (!pendingPaymentRestriction) return;
    const currentYmd = ymd(selectedDate);
    if (currentYmd > pendingPaymentRestriction.purchaseYmd) return;

    let candidate = parseYMD(pendingPaymentRestriction.minSelectableYmd);
    let guard = 0;
    while (guard < 60 && !isSelectableDate(candidate)) {
      const next = new Date(candidate.getTime());
      next.setDate(next.getDate() + 1);
      candidate = next;
      guard += 1;
    }

    if (guard >= 60) return;
    const candidateYmd = ymd(candidate);
    if (candidateYmd === currentYmd) return;

    setSelectedDate(candidate);
    setDayHours([]);
    setActiveHour("");
    if (selectedServiceId) {
      fetchWeeklyForDay(Number(selectedServiceId), candidateYmd);
    }
  }, [pendingPaymentRestriction, selectedDate, selectedServiceId]);

  // allowedServiceIds available via selectedProduct.serviceIds when needed

  // === Data fetching cacheado ===
  async function fetchWeeklyForDay(serviceId: number, dateStr: string) {
    const key = `${serviceId}|${dateStr}`;
    try {
      setSlotsLoading(true); setSlotsError(null);
      let all: WeeklyHour[] | undefined;
      if (cacheRef.current.weeklyByKey.has(key)) {
        all = cacheRef.current.weeklyByKey.get(key);
      } else {
        let pending = cacheRef.current.weeklyPromiseByKey.get(key);
        if (!pending) {
          pending = api.get(`/api/customers/reservations/weekly`, {
            params: { service_id: serviceId, date: dateStr, single_day: 1 }
          }).then(res => Array.isArray(res.data) ? res.data : []);
          cacheRef.current.weeklyPromiseByKey.set(key, pending);
        }
        all = await pending;
        cacheRef.current.weeklyPromiseByKey.delete(key);
        cacheRef.current.weeklyByKey.set(key, all);
      }

      const filtered = (all ?? [])
        .filter(x => x.date === dateStr)
        .map(x => ({
          ...x,
          free: Math.max(0, (x.capacity ?? 0) - (x.booked ?? 0)),
          isFull: (x.booked ?? 0) >= (x.capacity ?? 0)
        }))
        .sort((a, b) => a.hour.localeCompare(b.hour));
// 1) Pre-filtramos con /hourly → sólo dejar horas con datos (>0)
      const okSet = await prefilterHoursWithHourly(serviceId, dateStr, filtered.map(x => x.hour));

      // 2) Reconstruimos dayHours únicamente con las HH:MM válidas
      const filteredByHourly = filtered
        .filter(x => okSet.has(x.hour.slice(0, 5)))
        .sort((a, b) => a.hour.localeCompare(b.hour));

      setDayHours(filteredByHourly);
      setActiveHour("");
      const first = filtered[0]?.hour?.slice(0, 2);
      if (first) setTimeTab(Number(first) < 12 ? "am" : "pm");
    } catch (e: any) {
      cacheRef.current.weeklyPromiseByKey.delete(key);
      setSlotsError(e.message || "Error al consultar disponibilidad");
    } finally { setSlotsLoading(false); }
  }

  // Prefiltra horas consultando /api/customers/reservations/hourly para cada HH:MM.
// Devuelve un Set con las HH:MM que SÍ tienen datos (length > 0).
async function prefilterHoursWithHourly(serviceId: number, dateStr: string, hours: string[]) {
  const okHHmm = new Set<string>();

  // Usamos la misma bandera de carga de slots
  setSlotsLoading(true);
  setSlotsError(null);

  try {
    const promises = hours.map(async (h) => {
      const hhmm = h.slice(0, 5);
      const key = `${serviceId}|${dateStr}|${hhmm}`;
      let data: HourlyCoach[];

      // cache
      if (cacheRef.current.hourlyByKey.has(key)) {
        data = cacheRef.current.hourlyByKey.get(key)!;
      } else {
        const r = await api.get(`/api/customers/reservations/hourly`, { params: { service_id: serviceId, date: dateStr, time: hhmm } });
        data = Array.isArray(r.data) ? r.data : [];
        cacheRef.current.hourlyByKey.set(key, data);
      }

      if (Array.isArray(data) && data.length > 0) {
        okHHmm.add(hhmm);
      }
    });

    await Promise.all(promises);
    return okHHmm;
  } catch (err: any) {
    setSlotsError(err?.message || "Error al consultar horarios");
    return okHHmm;
  } finally {
    setSlotsLoading(false);
  }
}


  async function loadHourDetail(hourHHMMSS: string) {
    if (!selectedServiceId) return;
    const hhmm = hourHHMMSS.slice(0, 5);
    setHourDetail({ hour: hhmm, loading: true, error: null, coaches: [], chosenCoachId: null, manualCoachId: null });
    try {
            const key = `${selectedServiceId}|${ymd(selectedDate)}|${hhmm}`;
      let data: HourlyCoach[];

      // 👉 Intentar LEER de cache (ya llenada en el prefiltro)
      if (cacheRef.current.hourlyByKey.has(key)) {
        data = cacheRef.current.hourlyByKey.get(key)!;
      } else {
        // Fallback por seguridad (no debería ocurrir salvo navegación rara)
        const r = await api.get(`/api/customers/reservations/hourly`, {
          params: { service_id: selectedServiceId, date: ymd(selectedDate), time: hhmm }
        });
        data = Array.isArray(r.data) ? r.data : [];
        cacheRef.current.hourlyByKey.set(key, data);
      }


      const withFree = data.filter(c => (c.capacity ?? 0) - (c.booked ?? 0) > 0);

      const requireManual =
        isPhysioService(Number(selectedServiceId)) ||
        isVipManualEligible(Number(selectedServiceId), services, isVip);

      const isTraining = isTrainingService(Number(selectedServiceId), services);
      const prioritizedFavId = pickPreferredCoach(withFree, preferredCoaches, isTraining);
      const legacyFavId = !isTraining ? (favoriteByService[Number(selectedServiceId)] ?? null) : null;
      const preferredCoachId = prioritizedFavId ?? legacyFavId;

      let chosenId: number | null = null;
      let manualId: number | null = null;

      if (preferredCoachId && withFree.some(c => c.coach_id === preferredCoachId)) {
        if (requireManual) manualId = preferredCoachId;
        else chosenId = preferredCoachId;
      } else if (withFree.length > 0) {
        const fallbackId = withFree[0].coach_id;
        if (requireManual) manualId = fallbackId;
        else chosenId = fallbackId;
      }

      setHourDetail({
        hour: hhmm,
        loading: false,
        error: null,
        coaches: withFree,
        chosenCoachId: chosenId,
        manualCoachId: manualId,
      });
    } catch (e: any) {
      setHourDetail({ hour: "", loading: false, error: e.message || "Error al cargar detalle", coaches: [], chosenCoachId: null, manualCoachId: null });
    }
  }

  /* ====== Crear / Cancelar / Intercambiar / Reprogramar ====== */
  function getTimeslotIdForHour(hhmm: string, serviceId?: number) {
    const map = cacheRef.current.timeslotByHHMM;
    if (serviceId) {
      // attempt to resolve service name
      const svc = services.find(s => s.service_id === Number(serviceId));
      const sessionName = svc?.service_name ?? null;
      if (sessionName) {
        const key = `${sessionName}|${hhmm}`;
        if (map.has(key)) return map.get(key)!;
      }
    }
    // fallback to global mapping
    const globalKey = `__global__|${hhmm}`;
    return map.get(globalKey) ?? null;
  }

  async function ensureUserId() {
    if (userId) return userId;
    const user = await SecureStorage.getUser();
    if (!user?.id) throw new Error("Usuario no disponible.");
    setUserId(user.id);
    return user.id;
  }

  // Valida si un producto activo puede usarse en la fecha seleccionada
  // Busca exclusivamente por active_product_id (no usar product.id)
  function canBookWithProduct(activeProductId: number | ""): { ok: boolean; message?: string } {
    if (!activeProductId) return { ok: true };
    const wanted = Number(activeProductId);
    const prod: any = userProducts.find(p => Number(p.active_product_id) === wanted);
    if (!prod) return { ok: true };
    const expiryIso = prod.expiry_date ?? prod.expiryDate ?? null;
    const subscriptionStatus = prod.subscription_status ?? prod.subscriptionStatus ?? null;
    const typeOfProduct = prod.type_of_product ?? prod.typeOfProduct ?? null;
    if (!expiryIso) return { ok: true };
    const expiryYmd = apiYMD(String(expiryIso));
    const targetYmd = ymd(selectedDate);

    // Recurrent: si subscription_status === 'canceled' y fecha de reserva > expiry_date => bloquear
    if (String(typeOfProduct).toLowerCase() === "recurrent") {
      if (String(subscriptionStatus).toLowerCase() === "canceled" && expiryYmd && targetYmd > expiryYmd) {
        return { ok: false, message: `No puedes reservar: la suscripción está cancelada y expira el ${fmtDateLong(expiryYmd)}.` };
      }
      return { ok: true };
    }

    // Para bonos u otros tipos: si expiry_date < fecha intento reserva => bloquear
    if (expiryYmd && targetYmd > expiryYmd) {
      return { ok: false, message: `No puedes reservar: el bono expiró el ${fmtDateLong(expiryYmd)}.` };
    }
    return { ok: true };
  }

  async function createBooking() {

    if (bookingBusy) return;
    setBookingBusy(true);

    if (!selectedProductId) {
      sileo.warning("Selecciona un producto.");
      setBookingBusy(false);
      return;
    }
    if (!selectedServiceId) {
      sileo.warning("Selecciona un servicio.");
      setBookingBusy(false);
      return;
    }
    if (!hourDetail.hour) {
      sileo.warning("Selecciona una hora.");
      setBookingBusy(false);
      return;
    }

    const uid = await ensureUserId();
    // Validar producto/fecha antes de resolver timeslot
    const canBook = canBookWithProduct(selectedActiveProductId ?? "");
    if (!canBook.ok) {
      sileo.error(canBook.message || "No puedes reservar con este producto en esa fecha.");
      setBookingBusy(false);
      return;
    }

    const session_timeslot_id = getTimeslotIdForHour(hourDetail.hour, Number(selectedServiceId));
    if (!session_timeslot_id) {
      sileo.error("No se encontro el ID del horario.");
      setBookingBusy(false);
      return;
    }
    const alreadyHasBooking = bookings.some(b => {
      const sameDate = apiYMD(b.start_date) === ymd(selectedDate);
      const sameHour = b.start_time?.slice(0, 5) === hourDetail.hour;
      return b.status === "active" && sameDate && sameHour;
    });

    if (alreadyHasBooking) {
      sileo.warning("Ya tienes una reserva a esta hora.");
      setBookingBusy(false);
      return;
    }

    const anotherBookingSameDay = bookings.some(b =>
      b.status === "active" &&
      apiYMD(b.start_date) === ymd(selectedDate) &&
      String(b.service_name || "").toLowerCase().includes("entren")
    );
    if (!rescheduleFrom && anotherBookingSameDay && String(services.find(s => s.service_id === selectedServiceId)?.service_name || "").toLowerCase().includes("entren")) {
      sileo.warning("Ya tienes una reserva de entrenamiento este dia. No puedes reservar dos entrenamientos el mismo dia.");
      setBookingBusy(false);
      return;
    }

    const slotDateTime = new Date(`${ymd(selectedDate)}T${hourDetail.hour}:00`);
    const diffHours = (slotDateTime.getTime() - Date.now()) / 36e5;
    if (diffHours < 1) {
      sileo.warning("No se puede reservar con menos de 1 hora de antelacion.");
      setBookingBusy(false);
      return;
    }

    try {
      if (rescheduleFrom) {
        return await rescheduleByCancelCreate(rescheduleFrom.booking_id, uid, session_timeslot_id);
      } else {
        const payload = {
          active_product_id: selectedActiveProductId,
          product_id: selectedProductId,
          customer_id: uid,
          coach_id: hourDetail.manualCoachId ?? hourDetail.chosenCoachId ?? undefined,
          service_id: selectedServiceId,
          session_timeslot_id,
          start_date: ymd(selectedDate),
          amount: 0, discount: 0, total_amount: 0,
          coupon_id: null, payment_method: "cash",
          bank_account_id: null, card_id: null,
        };
        const res = await api.post(`/api/customers/reservations/bookings`, payload);
        const hasBookingId = res.data && typeof res.data === "object" && "booking_id" in res.data;
        const hasSuccess = res.data && typeof res.data === "object" && "success" in res.data && (res.data as any).success;
        if (!hasSuccess && !hasBookingId) throw new Error((res.data as any)?.error || "No se pudo crear la reserva");

        await refreshAfterMutation(uid);
        resetAndCloseModal();
        sileo.success("Reserva creada correctamente.");
        setBookingBusy(false);
      }
    } catch (e: any) {
      sileo.error(e.message || "Error al procesar la reserva");
      setBookingBusy(false);
    }
  }

  // recoverSession removed - not used in current UI. Recreate if recovery flow
  // needs to be re-enabled.

  async function cancelLocalBookingReminder(bookingId: number) {
    if (Capacitor.getPlatform() === "web") return;
    try {
      await cancelBookingReminder(bookingId);
    } catch (err) {
      console.warn("No se pudo cancelar el recordatorio local:", err);
    }
  }

  async function rescheduleByCancelCreate(oldBookingId: number, userIdLocal: number, session_timeslot_id: number) {
    setBookingBusy(true)
    const slotDateTime = new Date(`${ymd(selectedDate)}T${hourDetail.hour}:00`);
    const diffHours = (slotDateTime.getTime() - Date.now()) / 36e5;
    if (diffHours < 12) {
      sileo.warning("No se puede cambiar a una hora con menos de 12 horas de antelacion.");
      setBookingBusy(false);
      return;
    }

    if (!session_timeslot_id) {
      sileo.error("No se encontro el ID del horario.");
      setBookingBusy(false);
      return;
    }
    // Validación producto/fecha para reprogramar
    const canReschedule = canBookWithProduct(selectedActiveProductId ?? selectedProductId);
    if (!canReschedule.ok) {
      sileo.error(canReschedule.message || "No puedes reprogramar a esta fecha con ese producto.");
      setBookingBusy(false);
      return;
    }
    const alreadyHasBooking = bookings.some(b => {
      const sameDate = apiYMD(b.start_date) === ymd(selectedDate);
      const sameHour = b.start_time?.slice(0, 5) === hourDetail.hour;
      return b.status === "active" && sameDate && sameHour;
    });

    if (alreadyHasBooking) {
      sileo.warning("Ya tienes una reserva a esta hora.");
      setBookingBusy(false);
      return;
    }

    const hasDayBooking = bookings.some(b =>
      b.status === "active" &&
      b.booking_id !== oldBookingId &&
      apiYMD(b.start_date) === ymd(selectedDate) &&
      String(b.service_name || "").toLowerCase().includes("entren")
    );

    if (hasDayBooking && String(services.find(s => s.service_id === selectedServiceId)?.service_name || "").toLowerCase().includes("entren")) {
      sileo.warning("Ya tienes una reserva de entrenamiento este dia. No puedes reservar dos entrenamientos el mismo dia.");
      setBookingBusy(false);
      return;
    }

    try {
      await api.delete(`/api/customers/reservations/bookings/${oldBookingId}`);
      await cancelLocalBookingReminder(oldBookingId);
      const payload = {
        active_product_id: selectedActiveProductId,
        product_id: selectedProductId,
        customer_id: userIdLocal,
        coach_id: hourDetail.manualCoachId ?? hourDetail.chosenCoachId ?? undefined,
        service_id: selectedServiceId,
        session_timeslot_id,
        start_date: ymd(selectedDate),
        amount: 0, discount: 0, total_amount: 0,
        coupon_id: null, payment_method: "cash",
        bank_account_id: null, card_id: null,
      };
      const res = await api.post(`/api/customers/reservations/bookings`, payload);
      const ok = !!((res.data as any)?.success || (res.data as any)?.booking_id);
      if (!ok) throw new Error((res.data as any)?.error || (res.data as any)?.message || "No se pudo crear la reserva");

      await refreshAfterMutation(userIdLocal);
      resetAndCloseModal();
      sileo.success("Hora cambiada correctamente.");
    } catch (e: any) {
      sileo.error(e?.message || "No se pudo completar el cambio de hora.");
    }
    finally{
      setBookingBusy(false);
    }
  }

  async function cancelReservation(bookingId: number) {
    const booking = bookings.find(b => b.booking_id === bookingId);
    if (!booking) {
      sileo.error("No se encontro la reserva.");
      return;
    }
    const dateIso = apiYMD(booking.start_date);
    const hour = booking.start_time?.slice(0, 5) || "00:00";
    const startDateTime = new Date(`${dateIso}T${hour}:00`);
    const diffHours = (startDateTime.getTime() - Date.now()) / 36e5;

    if (diffHours < 12) {
      sileo.warning("No se puede cancelar una reserva con menos de 12 horas de antelacion.");
      return;
    }
    
    const ok = window.confirm("¿Seguro que quieres cancelar esta reserva?");
    if (!ok) return;
    setCancelBusyId(bookingId);
    try {
      const uid = await ensureUserId();
      await api.delete(`/api/customers/reservations/bookings/${bookingId}`);
      await cancelLocalBookingReminder(bookingId);
      await refreshAfterMutation(uid);
      sileo.success("Reserva cancelada correctamente.");
    } catch (e: any) {
      sileo.error(e?.message || "No se pudo cancelar la reserva");
    } finally {
      setCancelBusyId(null);
    }
  }

  async function swapAndCreate() {
    if (!swapCandidateId) return;
    if (!selectedProductId) {
      sileo.warning("Selecciona un producto.");
      return;
    }
    if (!selectedServiceId) {
      sileo.warning("Selecciona un servicio.");
      return;
    }
    if (!hourDetail.hour) {
      sileo.warning("Selecciona una hora.");
      return;
    }

    const uid = await ensureUserId();
    // Validar producto/fecha antes de proceder con swap
    const canSwap = canBookWithProduct(selectedActiveProductId ?? selectedProductId);
    if (!canSwap.ok) {
      sileo.error(canSwap.message || "No puedes mover/cambiar a esa fecha con ese producto.");
      return;
    }

  const session_timeslot_id = getTimeslotIdForHour(hourDetail.hour, Number(selectedServiceId));
    if (!session_timeslot_id) {
      sileo.error("No se encontro el ID del horario.");
      return;
    }

    const slotDateTime = new Date(`${ymd(selectedDate)}T${hourDetail.hour}:00`);
    const diffHours = (slotDateTime.getTime() - Date.now()) / 36e5;
    if (diffHours < 12) {
      setSwapError("No se puede cambiar a una hora con menos de 12 horas de antelación.");
      return;
    }

    if (!session_timeslot_id) {
      sileo.error("No se encontro el ID del horario.");
      return;
    }
    const alreadyHasBooking = bookings.some(b => {
      const sameDate = apiYMD(b.start_date) === ymd(selectedDate);
      const sameHour = b.start_time?.slice(0, 5) === hourDetail.hour;
      return b.status === "active" && sameDate && sameHour;
    });

    if (alreadyHasBooking) {
      sileo.warning("Ya tienes una reserva a esta hora.");
      setBookingBusy(false);
      return;
    }

    const hasDayBooking = bookings.some(b =>
      b.status === "active" &&
      b.booking_id !== swapCandidateId &&
      apiYMD(b.start_date) === ymd(selectedDate) &&
      String(b.service_name || "").toLowerCase().includes("entren")
    );
    if (hasDayBooking && String(services.find(s => s.service_id === selectedServiceId)?.service_name || "").toLowerCase().includes("entren")) {
      sileo.warning("Ya tienes otra reserva de entrenamiento en ese dia. No puedes mover la seleccionada ahi.");
      return;
    }

    function isSameWeek(d1: Date, d2: Date) {
      const monday = (date: Date) => {
        const d = new Date(date);
        const day = d.getDay() || 7;
        d.setDate(d.getDate() - day + 1);
        d.setHours(0, 0, 0, 0);
        return d;
      };
      return monday(d1).getTime() === monday(d2).getTime();
    }

    const newDate = new Date(`${ymd(selectedDate)}T${hourDetail.hour}:00`);
    const limitReached = serviceCredit?.remaining === 0;

    if (swapCandidateDate && !isSameWeek(swapCandidateDate, newDate) && limitReached) {
      sileo.warning("No puedes mover una sesion de otra semana a esta, porque ya alcanzaste el limite semanal.");
      return;
    }

    setSwapBusy(true);
    setSwapError(null);
    try {
      await api.delete(`/api/customers/reservations/bookings/${swapCandidateId}`);
      await cancelLocalBookingReminder(Number(swapCandidateId));

      const payload = {
        active_product_id: selectedActiveProductId,
        product_id: selectedProductId,
        customer_id: uid,
        coach_id: hourDetail.manualCoachId ?? hourDetail.chosenCoachId ?? undefined,
        service_id: selectedServiceId,
        session_timeslot_id,
        start_date: ymd(selectedDate),
        amount: 0,
        discount: 0,
        total_amount: 0,
        coupon_id: null,
        payment_method: "cash",
        bank_account_id: null,
        card_id: null,
      };
      const res = await api.post(`/api/customers/reservations/bookings`, payload);
      const ok = !!((res.data as any)?.success || (res.data as any)?.booking_id);
      if (!ok) throw new Error((res.data as any)?.error || (res.data as any)?.message || "No se pudo crear la reserva");

      await refreshAfterMutation(uid);
      resetAndCloseModal();
      sileo.success("Reserva cambiada correctamente.");
    } catch (e: any) {
      setSwapError(e?.response?.data?.error || e?.message || "No se pudo completar el cambio.");
    } finally {
      setSwapBusy(false);
    }
  }

  async function refreshAfterMutation(uid: number) {
    const [rC, rUB, rLim, rUP] = await Promise.all([
      api.get(`/api/customers/get/${uid}`),
      api.get(`/api/customers/reservations/user-bookings`, { params: { user_id: uid } }),
      api.get(`/api/customers/reservations/weekly-limit`, { params: { user_id: uid, target_date: ymd(selectedDate) } }),
      api.get(`/api/customers/catalog/user-products`),
    ]);

    const cJson: CustomerGetResponse = rC.data as CustomerGetResponse;
    setBookings(Array.isArray(cJson.data?.active_bookings) ? cJson.data.active_bookings : []);
    cacheRef.current.bookingsFetchedAt = Date.now();

    const ubs: UserBooking[] = Array.isArray(rUB.data) ? rUB.data : [];
    setUserBookings(ubs);

    const photoMap: Record<number, string | null> = {};
    for (const ub of ubs) photoMap[ub.id] = normalizePhotoUrl(ub.coach_profile_pic);
    setCoachPhotoByBooking(photoMap);

    if (rLim.data && typeof rLim.data === "object" && "weekly_limits" in rLim.data) {
      const limits = (rLim.data as { weekly_limits: WeeklyLimitEntry[] }).weekly_limits;
      const key = `${uid}|${ymd(selectedDate)}`;
      cacheRef.current.weeklyLimitsByDate.set(key, limits);
      setWeeklyLimits(limits);
    }

    setUserProducts(normalizeUserProducts(rUP.data));
  }

  function resetAndCloseModal() {
    setSwapMode(false);
    setSwapCandidateId("");
    setSwapError(null);
    setShowModal(false);
    setRescheduleFrom(null);
    setSelectedProductId("");
    setSelectedServiceId("");
    setRecoverMode(null);
    setDayHours([]);
    setActiveHour("");
    setHourDetail({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null, manualCoachId: null });
    setBookingBusy(false);
  }

  // NUEVO: abrir modal de reprogramación con producto/servicio bloqueados
  function openReschedule(b: ActiveBooking) {
    setRescheduleFrom(b);
    setSwapMode(false);
    setSwapCandidateId("");
    setSwapError(null);

    const dateIso = apiYMD(b.start_date);
    if (dateIso) setSelectedDate(parseYMD(dateIso));
    setSwapCandidateId(b.booking_id);

    // Obtener metadatos fiables desde /api/customers/reservations/user-bookings
    const meta = bookingMetaById.get(b.booking_id);
    const activeProdFromMeta = meta?.active_product_id ?? null;
    const prodIdFromMeta = meta?.product_id ?? null;
    const svcIdFromMeta  = meta?.service_id ?? null;

    if (activeProdFromMeta) {
      setSelectedProductId(activeProdFromMeta);
    } else if (prodIdFromMeta) {
      setSelectedProductId(prodIdFromMeta);
    } else {
      // Fallback por nombre (por si faltara product_id)
      const prod = userProducts.find((p) => p.name === b.product_name);
      if (prod) setSelectedProductId(prod.id);
    }

    if (svcIdFromMeta) {
      setSelectedServiceId(svcIdFromMeta);
    } else {
      // Fallback por nombre de servicio
      const svc = services.find((s) => s.service_name === b.service_name);
      if (svc) setSelectedServiceId(svc.service_id);
    }

    // Reset de estado de horas y detalle
    setDayHours([]);
    setActiveHour("");
    setHourDetail({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null, manualCoachId: null });

    const finalSvcId =
      svcIdFromMeta ??
      services.find((s) => s.service_name === b.service_name)?.service_id ??
      null;

    // Precargar horas del día original para ese servicio
    if (finalSvcId && dateIso) {
      fetchWeeklyForDay(finalSvcId, dateIso);
    }

    setShowModal(true);
  }

  // Límites y validaciones para el <input type="date">
  const dayToday = today.getDate();
  function lastDayOf(y: number, m: number) { return new Date(y, m + 1, 0); }
  const allowNextMonth = dayToday >= 15;

  const baseMaxSelectableDate = (() => {
    if (isVip) {
      const target = new Date(today.getFullYear(), today.getMonth() + 3, 1);
      return lastDayOf(target.getFullYear(), target.getMonth());
    }
    const target = allowNextMonth ? new Date(today.getFullYear(), today.getMonth() + 1, 1) : today;
    return lastDayOf(target.getFullYear(), target.getMonth());
  })();

  // Máximo seleccionable combinando la regla base y la antelación del producto
  const maxSelectableDate = (() => {
    const diaLimite = antelacion?.dias_antelacion_reserva ?? 0;
    if (diaLimite > 0) {
      const limitByDaysAhead = new Date(today.getTime());
      limitByDaysAhead.setHours(12, 0, 0, 0);
      limitByDaysAhead.setDate(limitByDaysAhead.getDate() + diaLimite);
      return limitByDaysAhead.getTime() > baseMaxSelectableDate.getTime()
        ? limitByDaysAhead
        : baseMaxSelectableDate;
    }
    return baseMaxSelectableDate;
  })();

  function isSelectableDate(d: Date) {
    const iso = ymd(d);
    if (iso < todayStr) return false;
    if (pendingPaymentRestriction && iso <= pendingPaymentRestriction.purchaseYmd) return false;
    if (iso > ymd(maxSelectableDate)) return false;
    if (d.getDay() === 0) return false; // domingos
    if (holidaySet.has(iso)) return false;

    if (!isVip) {
      const sameMonth =
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth();
      const isNextMonth =
        d.getFullYear() === nextMonth.getFullYear() &&
        d.getMonth() === nextMonth.getMonth();
      if (!sameMonth && !(allowNextMonth && isNextMonth)) return false;
    }
    return true;
  }

  /* =================== Render =================== */
  const offset = firstWeekdayOffset(currentYear, currentMonth);
  const totalDays = daysInMonth(currentYear, currentMonth);
  const cells: Array<{ day?: number; date?: string }> = [];
  for (let i = 0; i < offset; i++) cells.push({});
  for (let d = 1; d <= totalDays; d++) {
    const ds = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, date: ds });
  }
  const selectedYmd = ymd(selectedDate);
  const nowMadrid = new Date();
  const todayMadridStr = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" }).format(nowMadrid);
  const nowMadridHHMM = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Madrid",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).format(nowMadrid);

  const isBookingUpcoming = useCallback((booking: ActiveBooking) => {
    const reservaDate = apiYMD(booking.start_date);
    if (!reservaDate) return false;
    if (reservaDate < todayMadridStr) return false;
    if (reservaDate === todayMadridStr && booking.start_time) {
      const reservaHHMM = booking.start_time.slice(0, 5);
      const [startHourRaw, startMinuteRaw] = reservaHHMM.split(":").map(part => Number(part));
      const [nowHourRaw, nowMinuteRaw] = nowMadridHHMM.split(":").map(part => Number(part));

      if (
        Number.isFinite(startHourRaw) &&
        Number.isFinite(startMinuteRaw) &&
        Number.isFinite(nowHourRaw) &&
        Number.isFinite(nowMinuteRaw)
      ) {
        const startTotalMinutes = startHourRaw * 60 + startMinuteRaw;
        const nowTotalMinutes = nowHourRaw * 60 + nowMinuteRaw;
        if (nowTotalMinutes > startTotalMinutes + 60) return false;
      }
    }
    return true;
  }, [todayMadridStr, nowMadridHHMM]);

  const upcomingBookings = useMemo(
    () => allReservedSorted.filter(isBookingUpcoming),
    [allReservedSorted, isBookingUpcoming]
  );

  const pastBookings = useMemo(() => {
    const filtered = allReservedSorted.filter(b => !isBookingUpcoming(b));
    return filtered.reverse();
  }, [allReservedSorted, isBookingUpcoming]);

  const upcomingCount = upcomingBookings.length;
  
  const diaMax = maxSelectableDate;
  const minSelectableDateYmd =
    pendingPaymentRestriction && pendingPaymentRestriction.minSelectableYmd > todayStr
      ? pendingPaymentRestriction.minSelectableYmd
      : todayStr;

  return (
    <div style={pageOuter}>
      <div style={pageInner}>
        {/* Encabezado calendario */}
        <div style={calendarHeaderRow}>
          <button type="button" onClick={goPrevMonth} style={navBtnStyle} aria-label="Mes anterior">‹</button>
          <div style={calendarHeaderTitle}>{capitalize(monthLabel)}</div>
          <button type="button" onClick={goNextMonth} style={navBtnStyle} aria-label="Mes siguiente">›</button>
        </div>

        {/* Cabecera semana */}
        <div style={weekdaysRow}>
          {weekdaysShort.map(w => (
            <div key={`wd-${w}`} style={weekdayLabelStyle}>{w}</div>
          ))}
        </div>

        {/* Grilla días */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
          {cells.map((c, idx) => {
            const isSelected = c.date === selectedYmd;
            const isReserved = !!(c.date && datesWithSessions.has(c.date));
            const isToday = c.date === todayStr;
            const isPast = !!(c.date && c.date < todayStr);
            const isHoliday = !!(c.date && holidaySet.has(c.date));
            const isSunday = !!(c.date && parseYMD(c.date).getDay() === 0);

            const dateObj = c.date ? parseYMD(c.date) : null;
            let canSelect = false;

            const hasReservation = !!(c.date && datesWithSessions.has(c.date));
            const pendingBlocked = Boolean(
              pendingPaymentRestriction &&
              c.date &&
              !hasReservation &&
              c.date <= pendingPaymentRestriction.purchaseYmd
            );

            if (dateObj) {
              if (hasReservation) {
                canSelect = true;
              } else if (isVip) {
                canSelect = ymd(dateObj) <= ymd(maxSelectableDate);
              } else {
                const sameMonth =
                  dateObj.getFullYear() === today.getFullYear() &&
                  dateObj.getMonth() === today.getMonth();
                const isNextMonth2 =
                  dateObj.getFullYear() === nextMonth.getFullYear() &&
                  dateObj.getMonth() === nextMonth.getMonth();
                const after15 = today.getDate() >= 15;
                if (sameMonth) canSelect = true;
                else if (isNextMonth2 && after15) canSelect = true;
              }
            }

            const disabled =
              !c.day ||
              isHoliday ||
              isSunday ||
              (!hasReservation && isPast) ||
              !canSelect ||
              pendingBlocked;

            const dayTitle =
              pendingBlocked && pendingPaymentRestriction
                ? `Disponible a partir del ${fmtDateLong(pendingPaymentRestriction.minSelectableYmd)}`
                : undefined;

            return (
              <button
                type="button"
                key={`day-${c.date || idx}`}
                disabled={disabled}
                onClick={() => onDayClick(c.date, disabled)}
                title={dayTitle}
                style={{
                  ...dayCellStyle,
                  ...(isReserved ? dayCellReservedStyle : {}),
                  ...(isHoliday || isSunday ? dayCellHolidayStyle : {}),
                  ...(isSelected ? dayCellSelectedStyle : {}),
                  ...(isToday ? dayCellTodayStyle : {}),
                  opacity: c.day ? (disabled ? 0.45 : 1) : 0,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                {c.day || ""}
              </button>
            );
          })}
        </div>

        {/* Mensajes top */}
        {loading && <p style={{ marginTop: 12 }}>Cargando…</p>}
        {error && <p style={{ color: "red", marginTop: 12 }}>{error}</p>}

        {/* ===== Sesiones reservadas ===== */}
        {!loading && !error && (
          <div style={{ marginTop: 16 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 800 }}>Sesiones reservadas</h3>

            <div style={{ ...tabBar, margin: "0 0 12px" }}>
              <button
                type="button"
                onClick={() => setSessionsTab("upcoming")}
                style={{ ...tabBtn, ...(sessionsTab === "upcoming" ? tabBtnActive : {}) }}
              >
                Sesiones reservadas
              </button>
              <button
                type="button"
                onClick={() => setSessionsTab("past")}
                style={{ ...tabBtn, ...(sessionsTab === "past" ? tabBtnActive : {}) }}
              >
                Sesiones Pasadas
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>
                {sessionsTab === "upcoming" ? "Tus sesiones reservadas" : "Historial de sesiones"}
              </h3>
              <span style={{ color: "#6b7280" }}>
                — {sessionsTab === "upcoming" ? `${upcomingCount} próximas` : `${pastBookings.length} registradas`}
              </span>
            </div>

            {sessionsTab === "upcoming" ? (
              upcomingCount === 0 ? (
                <p style={{ color: "#6b7280" }}>No tienes sesiones reservadas.</p>
              ) : (
                upcomingBookings.map(b => {
                  const photo = coachPhotoByBooking[b.booking_id] ?? null;
                  const minHorasCambio = antelacion?.horas_antelacion_cambio ?? 0;
                  const hoursToStart = hoursUntil(apiYMD(b.start_date), b.start_time);
                  const canReschedule = hoursToStart >= minHorasCambio;
                  const bookingMeta = bookingMetaById.get(b.booking_id);
                  const trainingById = bookingMeta?.service_id
                    ? isTrainingService(bookingMeta.service_id, services)
                    : false;
                  const trainingByName = !trainingById && isTrainingServiceName(b.service_name);
                  const isTrainingBooking = trainingById || trainingByName;
                  const hideCoachInfo = isTrainingBooking && hoursToStart > 1;
                  const coachLabel = hideCoachInfo ? "Se asignará 1 hora antes" : (b.coach_name || "—");

                  return (
                    <div key={`bk-${b.booking_id}`} style={cardStyle}>
                      <div style={{ display: "flex", gap: 12 }}>
                        {hideCoachInfo ? (
                          <div style={avatarFallback}></div>
                        ) : photo ? (
                          <img src={photo} alt={`Foto de ${b.coach_name}`} style={avatarImg} />
                        ) : (
                          <div style={avatarFallback}>{initialsFromName(b.coach_name)}</div>
                        )}

                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <span style={{ fontWeight: 700 }}>{fmtDateLong(apiYMD(b.start_date))}</span>
                            {b.start_time && (
                              <span style={{ fontSize: 12, color: "#374151" }}>· {b.start_time.slice(0, 5)}h</span>
                            )}
                          </div>
                          <div style={{ fontSize: 14 }}>
                            <div>🔔 Servicio: <strong>{b.service_name || "—"}</strong></div>
                            <div>🧑‍🏫 Profesional: <strong>{coachLabel}</strong></div>
                            {hideCoachInfo && (
                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                Se mostrará el entrenador cuando falte 1 hora para la sesión.
                              </div>
                            )}
                          </div>

                          <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!canReschedule) {
                                  sileo.warning("No puedes cambiar esta reserva: faltan menos de 12 horas para el inicio.");
                                  return;
                                }
                                openReschedule(b);
                              }}
                              style={{
                                ...outlineBtnStyle,
                                ...(canReschedule ? {} : { opacity: .5, cursor: "not-allowed" })
                              }}
                              aria-label="Cambiar hora de esta reserva"
                              disabled={cancelBusyId === b.booking_id || !canReschedule}
                              title={canReschedule ? "Cambiar hora" : `No se puede cambiar con menos de ${minHorasCambio} horas de antelación.`}
                            >
                              Cambiar hora
                            </button>

                            <button
                              type="button"
                              onClick={() => cancelReservation(b.booking_id)}
                              style={{ ...outlineBtnStyle, borderColor: "#ef4444", color: "#b91c1c" }}
                              aria-label="Cancelar esta reserva"
                              disabled={cancelBusyId === b.booking_id}
                            >
                              {cancelBusyId === b.booking_id ? "Cancelando…" : "Cancelar"}
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                const dateStr = apiYMD(b.start_date);
                                const timeStr = (b.start_time || "00:00").slice(0, 5);
                                const start = new Date(`${dateStr}T${timeStr}:00`);
                                sileo.info("Si modificas tu sesion despues de descargar el evento, no se actualizara tu calendario.");
                                shareICS(b.service_name || "Evento", start);
                              }}
                              style={{ ...outlineBtnStyle, borderColor: "#57bb6dff", color: "#287e4cff" }}
                            >
                              Descargar evento
                            </button>

                            {!canReschedule && (
                              <span style={{ fontSize: 12, color: "#6b7280" }}>
                                No se puede cambiar con menos de {minHorasCambio} horas de antelación.
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )
            ) : pastBookings.length === 0 ? (
              <p style={{ color: "#6b7280" }}>No hay sesiones pasadas disponibles.</p>
            ) : (
              pastBookings.map(b => {
                const photo = coachPhotoByBooking[b.booking_id] ?? null;
                const paymentLabel = paymentStatusLabel(b.payment_status);

                return (
                  <div key={`bk-past-${b.booking_id}`} style={{ ...cardStyle, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                    <div style={{ display: "flex", gap: 12 }}>
                      {photo ? (
                        <img src={photo} alt={`Foto de ${b.coach_name}`} style={avatarImg} />
                      ) : (
                        <div style={avatarFallback}>{initialsFromName(b.coach_name)}</div>
                      )}

                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <span style={{ fontWeight: 700 }}>{fmtDateLong(apiYMD(b.start_date))}</span>
                          {b.start_time && (
                            <span style={{ fontSize: 12, color: "#374151" }}>· {b.start_time.slice(0, 5)}h</span>
                          )}
                        </div>
                        <div style={{ fontSize: 14 }}>
                          <div>🔔 Servicio: <strong>{b.service_name || "—"}</strong></div>
                          <div>🧑‍🏫 Profesional: <strong>{b.coach_name || "—"}</strong></div>
                        </div>

                        {paymentLabel && (
                          <div style={{ marginTop: 6, fontSize: 12, color: "#374151" }}>
                            💳 Pago: <strong>{paymentLabel}</strong>
                          </div>
                        )}

                        <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <span style={historyTagStyle}>⏱️ Tarde: <strong>{Number(b.is_customer_late) ? "Sí" : "No"}</strong></span>
                          <span style={historyTagStyle}>🚫 Ausente: <strong>{Number(b.is_customer_absent) ? "Sí" : "No"}</strong></span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* ===== Modal reservar ===== */}
      {showModal && (
        <div style={sheetOverlayStyle} role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div style={sheetCardStyle}>
            <ModalHeader onBack={resetAndCloseModal} />
            <div style={sheetContentStyle}>
              {/* Producto + Servicio */}
              <section style={{ marginBottom: 16 }}>
                <h4 style={sectionTitle}>Producto</h4>
                <select
                  value={selectedProductId}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value ? Number(e.target.value) : "");
                    setSelectedServiceId("");
                    setDayHours([]);
                    setSwapCandidateId("");
                    setSwapMode(false);
                    setActiveHour("");
                  }}
                  style={pillSelect}
                  disabled={!!rescheduleFrom || !!recoverMode}
                  title={rescheduleFrom ? "No modificable al reprogramar" : ""}
                >
                  <option key="prod-ph" value="">
                    {userProducts.length > 0 ? "Seleccione un producto…" : "No tienes productos contratados"}
                  </option>
                  {userProducts.map((p, idx) => {
                    const value = p.active_product_id ?? p.id;
                    return (
                      <option key={`p-${value}-${idx}`} value={value}>
                        {p.name}
                      </option>
                    );
                  })}
                </select>

                <div style={{ height: 10 }} />

                <h4 style={sectionTitle}>Servicio</h4>
                <select
                  value={selectedServiceId}
                  onChange={(e) => {
                    const v = e.target.value ? Number(e.target.value) : "";
                    setSelectedServiceId(v);
                    setDayHours([]);
                    setActiveHour("");
                    setSwapCandidateId("");
                    setSwapMode(false);
                    if (v) fetchWeeklyForDay(v, ymd(selectedDate));
                  }}
                  style={pillSelect}
                  disabled={!!rescheduleFrom || !selectedProductId}
                  title={rescheduleFrom ? "No modificable al reprogramar" : ""}
                >
                  <option key="svc-ph" value="">{selectedProductId ? "Seleccione un servicio…" : "Elige primero un producto"}</option>
                  {servicesForProduct.map(s => (<option key={`svc-${s.service_id}`} value={s.service_id}>{s.service_name}</option>))}
                </select>

                {/* Indicador de crédito usando /user-weekly-limit */}
                {!rescheduleFrom && selectedServiceId && serviceCredit && !recoverMode && (
                  <div style={{ marginTop: 8 }}>
                    {serviceCredit.mode === "weekly" ? (
                      serviceCredit.remaining > 0 ? (
                        <span style={creditPillOk}>
                          Quedan {serviceCredit.remaining} / {serviceCredit.limit} esta semana
                        </span>
                      ) : (
                        <span style={creditPillAlert}>
                          Has alcanzado el límite semanal para este producto
                        </span>
                      )
                    ) : serviceCredit.remaining > 0 ? (
                      <span style={creditPillOk}>
                        Quedan {serviceCredit.remaining} / {serviceCredit.limit} sesiones disponibles
                      </span>
                    ) : (
                      <span style={creditPillAlert}>
                        Ya no te quedan sesiones disponibles de este producto
                      </span>
                    )}
                  </div>
                )}
              </section>

              {/* Horario */}
              <section>
                <h4 style={sectionTitle}>Horario</h4>
                <div style={{ color: "#9ca3af", fontWeight: 600, marginBottom: 8 }}>Seleccione fecha y hora</div>
                <div style={dateRow}>
                  <input
                    type="date"
                    value={ymd(selectedDate)}
                    min={minSelectableDateYmd}
                    max={ymd(diaMax)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) return;
                      if (pendingPaymentRestriction && value <= pendingPaymentRestriction.purchaseYmd) {
                        e.currentTarget.value = ymd(selectedDate);
                        sileo.warning(`Este producto tiene el pago pendiente. Podras reservar a partir del ${fmtDateLong(pendingPaymentRestriction.minSelectableYmd)}.`);
                        return;
                      }
                      const d = parseYMD(value);
                      if (!isSelectableDate(d)) {
                        e.currentTarget.value = ymd(selectedDate);
                        sileo.warning("Fecha no disponible (pasada, festivo, domingo o fuera del rango permitido).");
                        return;
                      }
                      setSelectedDate(d);
                      setDayHours([]);
                      setActiveHour("");
                      if (selectedServiceId) fetchWeeklyForDay(Number(selectedServiceId), ymd(d));
                    }}
                    style={dateInput}
                  />
                </div>
                {pendingPaymentRestriction && (
                  <div style={{ marginTop: 6, fontSize: 13, color: "#b91c1c", fontWeight: 600 }}>
                    Este producto tiene el pago pendiente. Podras reservar a partir del {fmtDateLong(pendingPaymentRestriction.minSelectableYmd)}.
                  </div>
                )}

                <div style={tabBar}>
                  <button type="button" onClick={() => setTimeTab("am")} style={{ ...tabBtn, ...(timeTab === "am" ? tabBtnActive : {}) }}>Mañana</button>
                  <button type="button" onClick={() => setTimeTab("pm")} style={{ ...tabBtn, ...(timeTab === "pm" ? tabBtnActive : {}) }}>Tarde</button>
                </div>

                <div style={{ minHeight: 90 }}>
                  {slotsLoading && <p>Cargando horas…</p>}
                  {slotsError && <p style={{ color: "red" }}>{slotsError}</p>}
                  {!slotsLoading && !slotsError && (
                    <>
                      {dayHours.length === 0 && <p style={{ color: "#6b7280" }}>No hay profesionales disponibles en esta fecha.</p>}
                      {dayHours.length > 0 && (
                        <div style={hoursGrid}>
                          {dayHours
                            .filter(s => {
                                  const [h, m] = s.hour.split(":").map(Number);
                                  if (timeTab === "am") {
                                      // Mañana: hasta las 13:30 inclusive
                                      return h < 13 || (h === 13 && m <= 30);
                                  } else {
                                      // Tarde: desde las 14:00 en adelante
                                      return h > 13 || (h === 13 && m > 30);
                                  }
                              })
                            .map(slot => {
                              function toMinutes(hhmm: string) {
                                const [h, m] = hhmm.split(":").map(Number);
                                return h * 60 + m;
                              }
                              const isToday = slot.date === todayStr;
                              const hhmm = slot.hour.slice(0, 5);
                              const now = new Date();
                              const nowMins = now.getHours() * 60 + now.getMinutes();
                              const slotMins = toMinutes(hhmm);
                              const isPastHour = isToday && slotMins <= nowMins;

                              // Mantiene tu lógica de 12h, solo para tooltip
                              const slotDateTime = new Date(`${slot.date}T${hhmm}:00`);
                              const diffHours = (slotDateTime.getTime() - Date.now()) / 36e5;
                              const tooSoon = diffHours < 1;

                              const isFull = Number(slot.booked ?? 0) >= Number(slot.capacity ?? 1);

                              return (
                                <button
                                  type="button"
                                  key={`slot-${slot.date}-${slot.hour}-${slot.service_id}`}
                                  onClick={() => {
                                    if (!isPastHour) {
                                      setActiveHour(hhmm);
                                      loadHourDetail(slot.hour);
                                    }
                                  }}
                                  disabled={isPastHour || isFull || tooSoon}
                                  style={{
                                    ...timeBtn,
                                    ...(activeHour === hhmm
                                      ? { border: "2px solid #d11f2a", boxShadow: "0 0 0 2px rgba(209,31,42,.15)" }
                                      : { border: "1px solid #e5e7eb", boxShadow: "none" }),
                                    ...(isPastHour ? { opacity: 0.5, cursor: "not-allowed" } : {})
                                  }}
                                  title={

                                    isFull
                                      ? "Hora completa"
                                      : tooSoon
                                      ? `No se puede reservar con menos de 1 hora de antelación`
                                      : isPastHour
                                      ? "Hora pasada, no disponible"
                                      : ""
                                  }
                                >
                                  {hhmm}
                                </button>
                              );
                            })}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Selección de profesional SOLO fisioterapia o VIP manual */}
                {(() => {
                  const requiresManual =
                    !!selectedServiceId &&
                    (isPhysioService(Number(selectedServiceId)) ||
                      isVipManualEligible(Number(selectedServiceId), services, isVip));

                  if (!requiresManual || !hourDetail.hour || hourDetail.loading || hourDetail.error) return null;

                  const label =
                    isPhysioService(Number(selectedServiceId))
                      ? "Selecciona profesional (fisioterapia)"
                      : "Selecciona profesional (VIP)";

                  return (
                    <div style={{ marginTop: 14 }}>
                      <label style={{ display: "block", fontSize: 13, color: "#6b7280", marginBottom: 6 }}>
                        {label}
                      </label>
                      <select
                        value={hourDetail.manualCoachId ?? ""}
                        onChange={(e) =>
                          setHourDetail(h => ({ ...h, manualCoachId: e.target.value ? Number(e.target.value) : null }))
                        }
                        style={pillSelect}
                      >
                        <option value="">Elige un profesional...</option>
                        {hourDetail.coaches.map(c => (
                          <option key={`coach-${c.coach_id}`} value={c.coach_id}>{c.coach_name}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {/* ==== BLOQUE DE CAMBIO DE RESERVA ==== */}
                {!rescheduleFrom && selectedServiceId && serviceCredit && Number.isFinite(serviceCredit.remaining) && serviceCredit.remaining === 0 && (
                  <div style={{ marginTop: 14, padding: 12, border: "1px solid #fecaca", background: "#fef2f2", borderRadius: 10 }}>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>
                      {serviceCredit.mode === "weekly"
                        ? "Has alcanzado el límite semanal"
                        : "No te quedan sesiones disponibles en este bono"}
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      {serviceCredit.mode === "weekly"
                        ? <>Elige una de tus reservas de <strong>esta semana</strong> para cancelarla y usarla aquí:</>
                        : serviceCredit.mode === "total"
                          ? <>Elige una reserva futura de <strong>cualquiera de los servicios del pool</strong> para cancelarla y usarla aquí:</>
                          : <>Elige una reserva futura del <strong>mismo bono</strong> para cancelarla y usarla aquí:</>}
                    </div>

                    {swapCandidates.length === 0 ? (
                      <div style={{ color: "#6b7280" }}>
                        {serviceCredit.mode === "weekly"
                          ? "No tienes reservas del mismo bono en esta semana."
                          : "No tienes reservas futuras intercambiables en este bono."}
                      </div>
                    ) : (
                      <>
                        <select
                          value={swapCandidateId}
                          onChange={(e) => {
                            const id = e.target.value ? Number(e.target.value) : "";
                            setSwapCandidateId(id);
                            if (id) {
                              const found = swapCandidates.find(c => c.id === id);
                              setSwapCandidateDate(found ? new Date(found.start_date) : null);
                            } else {
                              setSwapCandidateDate(null);
                            }
                          }}
                          style={pillSelect}
                        >
                          <option value="">Selecciona una reserva…</option>
                          {swapCandidates.map(c => (
                            <option key={c.id} value={c.id}>{c.label}</option>
                          ))}
                        </select>

                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <button
                            type="button"
                            onClick={() => { setSwapMode(true); swapAndCreate(); }}
                            disabled={
                              swapBusy ||
                              !hourDetail.hour ||
                              !swapCandidateId ||
                              (() => {
                                const requiresManual =
                                  !!selectedServiceId &&
                                  (isPhysioService(Number(selectedServiceId)) ||
                                    isVipManualEligible(Number(selectedServiceId), services, isVip));
                                return requiresManual && !hourDetail.manualCoachId;
                              })()
                            }
                            style={{
                              ...reserveCta,
                              ...(swapBusy ||
                                !hourDetail.hour ||
                                !swapCandidateId ||
                                ([3, 19].includes(Number(selectedServiceId)) && !hourDetail.manualCoachId)
                                ? reserveCtaDisabled : {}),
                            }}
                          >
                            {swapBusy ? "Cambiando…" : "Cancelar seleccionada y reservar esta"}
                          </button>
                        </div>

                        {!hourDetail.hour && (
                          <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                            Selecciona una <strong>hora</strong> para activar el cambio.
                          </div>
                        )}
                      </>
                    )}

                    {swapError && <div style={{ color: "#b91c1c", marginTop: 8 }}>{swapError}</div>}
                  </div>
                )}
              </section>
            </div>
            {(!serviceCredit || !Number.isFinite(serviceCredit.remaining) || serviceCredit.remaining > 0 || rescheduleFrom) && (
              <div style={sheetBottomBar}>
                <button
                  type="button"
                  onClick={createBooking}
                  disabled={
                    bookingBusy ||
                    !selectedProductId ||
                    !selectedServiceId ||
                    !hourDetail.hour ||
                    ([3, 19].includes(Number(selectedServiceId)) && !hourDetail.manualCoachId)
                  }
                  style={{
                    ...reserveCta,
                    ...(bookingBusy ||
                      !selectedProductId ||
                      !selectedServiceId ||
                      !hourDetail.hour ||
                      ([3, 19].includes(Number(selectedServiceId)) && !hourDetail.manualCoachId)
                      ? reserveCtaDisabled : {}),
                  }}
                >
                  {rescheduleFrom ? "Cambiar hora" : recoverMode ? "Recuperar" : "Reservar"}
                </button>
              </div>
            )}

          </div>
        </div>
      )}

      {/* ===== Modal historial (DETALLADO) ===== */}
      {showPastModal && (
        <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-labelledby="past-modal-title">
          <div style={modalCardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 id="past-modal-title" style={{ margin: 0, fontSize: 18 }}>Sesiones — {fmtDateLong(ymd(selectedDate))}</h3>
              <button type="button" onClick={() => setShowPastModal(false)} style={closeBtnStyle} aria-label="Cerrar">✕</button>
            </div>

            {pastLoading && <p>Cargando…</p>}
            {pastError && <p style={{ color: "red" }}>{pastError}</p>}
            {!pastLoading && !pastError && (
              <>
                {pastSessions.length === 0 && <p style={{ color: "#6b7280" }}>No se encontraron sesiones.</p>}
                {pastSessions.map(s => (
                  <div key={`past-${s.booking_id}`} style={cardStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <div style={{ fontWeight: 700 }}>{s.time ? `${s.time}h` : "—"}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>#{s.booking_id}</div>
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                      <div>🧩 Producto: <strong>{s.product_name || "—"}</strong></div>
                      <div>🔔 Servicio: <strong>{s.service_name || "—"}</strong></div>
                      <div>🧑‍🏫 Profesional: <strong>{s.coach_name || "—"}</strong></div>
                      {(s.payment_status === "paid" || s.payment_status === "Pagado") && (
                        <div>💳 Pago: <strong>Pagado</strong></div>
                      )}
                      {(s.payment_status === "pending" || s.payment_status === "Pendiente") && (
                        <div>💳 Pago: <strong>Pendiente</strong></div>
                      )}
                      {(s.payment_status === "cancelled" || s.payment_status === "Cancelado") && (
                        <div>💳 Pago: <strong>Cancelado</strong></div>
                      )}
                      {s.comment && <div>📝 Comentario: <em>{s.comment}</em></div>}
                      <div style={{ marginTop: 6, display: "flex", gap: 10 }}>
                        <span title="Llegó tarde" style={{ padding: "2px 8px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#f9fafb" }}>
                          ⏱️ Tarde: <strong>{s.is_customer_late ? "Sí" : "No"}</strong>
                        </span>
                        <span title="Ausencia" style={{ padding: "2px 8px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#f9fafb" }}>
                          🚫 Ausente: <strong>{s.is_customer_absent ? "Sí" : "No"}</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== Cuestionario ===== */}
      {cuestionarioActivo && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              maxWidth: 400,
              width: "90%",
            }}
          >
            <h3>Cuestionario Pre-Entreno</h3>
            <p style={{ fontWeight: "bold" }}>¿Cómo te encuentras en general?</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button type="button" onClick={() => responderPregunta("Muy bien")} style={cuestionarioBtn}>⏫ Muy bien</button>
              <button type="button" onClick={() => responderPregunta("Bien")} style={cuestionarioBtn}>⬆️ Bien</button>
              <button type="button" onClick={() => responderPregunta("Normal")} style={cuestionarioBtn}>➖ Normal</button>
              <button type="button" onClick={() => responderPregunta("Mal")} style={cuestionarioBtn}>⬇️ Mal</button>
              <button type="button" onClick={() => responderPregunta("Muy mal")} style={cuestionarioBtn}>⏬ Muy mal</button>
            </div>

            <div style={{ marginTop: 12, textAlign: "right" }}>
              <button type="button" onClick={omitirFormulario} style={{ color: "#d11f2a" }}>Omitir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =================== Helpers =================== */
function normalizeUserProducts(raw: any): UserProduct[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((p: any) => ({
    id: p.id ?? p.product_id,
    name: p.name ?? p.product_name,
    active_product_id: p.active_product_id ?? null,
    price: p.price ?? p.sell_price ?? null,
    serviceIds: Array.isArray(p.serviceIds) ? p.serviceIds.map(Number)
      : Array.isArray(p.service_ids) ? p.service_ids.map(Number)
        : [],
    // Preserve expiry / subscription fields if present (added to userProducts schema)
    expiry_date: p.expiry_date ?? p.expiryDate ?? null,
    subscription_status: p.subscription_status ?? p.subscriptionStatus ?? null,
    type_of_product: p.type_of_product ?? p.typeOfProduct ?? null,
    payment_status: p.payment_status ?? p.paymentStatus ?? null,
    purchase_date: p.purchase_date ?? p.purchaseDate ?? null,
    sessionsRemaining:
      (typeof p.sesions === "number" ? p.sesions :
        typeof p.sessions === "number" ? p.sessions :
          typeof p.remaining_sessions === "number" ? p.remaining_sessions :
            typeof p.sessions_remaining === "number" ? p.sessions_remaining : null),
    centro: p.centro ?? null,
  })).filter(x => x.id && x.name);
}

function normalizePreferredCoaches(raw: any): PreferredCoachItem[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .filter((item: any) => Number.isInteger(item?.coach_id))
    .map((item: any) => ({
      coach_id: Number(item.coach_id),
      priority: Number.isInteger(item.priority) ? Number(item.priority) : null,
    }))
    .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))
    .slice(0, 3)
    .map((item, idx) => ({ ...item, priority: idx + 1 }));
}

function hoursUntil(dateIso: string, timeHHMMSS?: string) {
  const hhmmss = (timeHHMMSS || "00:00:00").slice(0, 8);
  const start = new Date(`${dateIso}T${hhmmss}`); // local TZ
  return (start.getTime() - Date.now()) / 36e5;   // ms → horas
}

function isPhysioService(id?: number | string) {
  const num = Number(id);
  return [3, 19].includes(num);
}

function isTrainingService(id: number | string, servicesList: ServiceItem[]) {
  const svc = servicesList.find(s => Number(s.service_id) === Number(id));
  const name = (svc?.service_name || "").toLowerCase();
  if (!name || isPhysioService(id)) return false;
  return name.includes("entren") && !name.includes("nutric");
}

function isTrainingServiceName(serviceName?: string | null) {
  const normalized = (serviceName || "").toLowerCase();
  if (!normalized) return false;
  if (normalized.includes("nutric")) return false;
  if (normalized.includes("fisio")) return false;
  return normalized.includes("entren");
}

function pickPreferredCoach(coaches: HourlyCoach[], favorites: PreferredCoachItem[], isTraining: boolean) {
  if (!isTraining || !Array.isArray(coaches) || coaches.length === 0) return null;
  if (!Array.isArray(favorites) || favorites.length === 0) return null;

  for (const fav of favorites) {
    const match = coaches.find(c => c.coach_id === fav.coach_id);
    if (match) return fav.coach_id;
  }
  return null;
}

function isVipManualEligible(
  id: number | string,
  servicesList: ServiceItem[],
  vipFlag: boolean
) {
  if (!vipFlag) return false;
  const svc = servicesList.find(s => Number(s.service_id) === Number(id));
  const name = (svc?.service_name || "").toLowerCase();
  return name.includes("entren") || name.includes("nutric");
}

/* =================== Estilos =================== */
const pageOuter: React.CSSProperties = {
  position: "relative",
  minHeight: "100vh",
  paddingBottom: 60,
  background:
    "radial-gradient(circle at top right, rgba(56, 189, 248, 0.16), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)"
};
const pageInner: React.CSSProperties = { padding: "12px" };
const calendarHeaderRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 10
};
const calendarHeaderTitle: React.CSSProperties = { fontWeight: 800, color: "#0f172a", fontSize: 18 };
const weekdaysRow: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 8 };
const weekdayLabelStyle: React.CSSProperties = { textAlign: "center", fontSize: 12, color: "#64748b", fontWeight: 700 };
const navBtnStyle: React.CSSProperties = {
  width: 38, height: 34, borderRadius: 12, background: "#ffffff", fontSize: 18, lineHeight: "30px", cursor: "pointer",
  border: "1px solid rgba(191, 219, 254, 0.9)",
  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
  color: "#1d4ed8"
};
const dayCellStyle: React.CSSProperties = {
  height: 38, padding: 0, borderRadius: 12, background: "rgba(255, 255, 255, 0.92)",
  border: "1px solid rgba(191, 219, 254, 0.72)",
  color: "#0f172a", fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.04)"
};
const dayCellReservedStyle: React.CSSProperties = { background: "#dbeafe", border: "1px solid #93c5fd", color: "#1d4ed8" };
const dayCellHolidayStyle: React.CSSProperties = { background: "#0f172a", color: "#e2e8f0", border: "1px solid #0f172a" };
const dayCellSelectedStyle: React.CSSProperties = { background: "linear-gradient(135deg, #1d4ed8 0%, #0f172a 100%)", color: "#fff", border: "1px solid #1d4ed8" };
const dayCellTodayStyle: React.CSSProperties = { border: "3px solid #f97316" };

const cardStyle: React.CSSProperties = {
  padding: 14,
  borderRadius: 16,
  background: "rgba(255, 255, 255, 0.92)",
  border: "1px solid rgba(191, 219, 254, 0.78)",
  marginBottom: 12,
  boxShadow: "0 14px 28px rgba(15, 23, 42, 0.06)"
};

const sheetOverlayStyle: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(15,23,42,.42)", display: "flex", justifyContent: "center", alignItems: "stretch", zIndex: 2100, backdropFilter: "blur(6px)" };
const modalOverlayStyle: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(15,23,42,.42)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2200, backdropFilter: "blur(6px)" };
const modalCardStyle: React.CSSProperties = { width: "100%", maxWidth: 500, background: "#fff", borderRadius: 20, padding: "24px 18px", boxShadow: "0 24px 60px rgba(15,23,42,.22)", maxHeight: "90vh", overflowY: "auto", border: "1px solid rgba(191,219,254,.6)" };
const sheetCardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 600,
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  maxHeight: "95vh",
  borderRadius: "20px",
  border: "1px solid rgba(191,219,254,.6)",
  overflow: "hidden"
};

const sheetContentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "16px 16px 90px"
};
const sheetBottomBar: React.CSSProperties = { position: "sticky", bottom: 0, left: 0, right: 0, padding: "12px 16px calc(env(safe-area-inset-bottom, 0px) + 12px)", background: "rgba(255,255,255,.96)", boxShadow: "0 -12px 28px rgba(15,23,42,.08)" };

const sectionTitle: React.CSSProperties = { margin: "0 0 10px", fontSize: 18, fontWeight: 800, color: "#0f172a" };
const pillSelect: React.CSSProperties = { width: "100%", borderRadius: 14, border: "1px solid rgba(191,219,254,.9)", padding: "12px 14px", fontSize: 16, background: "#fff", color: "#0f172a", boxShadow: "0 8px 18px rgba(15,23,42,.04)" };

const dateRow: React.CSSProperties = { position: "relative", display: "flex", alignItems: "center", marginBottom: 12 };
const dateInput: React.CSSProperties = { width: "100%", borderRadius: 14, border: "1px solid rgba(191,219,254,.9)", padding: "12px 44px 12px 14px", fontSize: 16, boxShadow: "0 8px 18px rgba(15,23,42,.04)" };

const tabBar: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, borderBottom: "1px solid #e5e7eb", margin: "6px 0 10px" };
const tabBtn: React.CSSProperties = { background: "transparent", borderWidth: 0, padding: "10px 4px", fontWeight: 700, color: "#9ca3af", borderBottom: "2px solid transparent", cursor: "pointer" };
const tabBtnActive: React.CSSProperties = { color: "#1d4ed8", borderBottom: "2px solid #1d4ed8" };

const hoursGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const timeBtn: React.CSSProperties = { padding: "14px 10px", borderRadius: 14, background: "#fff", border: "1px solid rgba(191,219,254,.9)", fontSize: 18, fontWeight: 700, cursor: "pointer", boxShadow: "0 8px 18px rgba(15,23,42,.04)" };

const reserveCta: React.CSSProperties = { width: "100%", padding: "16px 14px", borderRadius: 999, background: "linear-gradient(135deg, #f97316 0%, #dc2626 100%)", color: "#fff", borderWidth: 0, fontWeight: 800, fontSize: 20, cursor: "pointer", boxShadow: "0 14px 28px rgba(249,115,22,.24)" };
const reserveCtaDisabled: React.CSSProperties = { opacity: .5, cursor: "not-allowed" };

const closeBtnStyle: React.CSSProperties = {
  width: 36, height: 36, borderRadius: 10, background: "#eaf2ff", color: "#1d4ed8",
  border: "none", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
};
const historyTagStyle: React.CSSProperties = {
  padding: "2px 8px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#f9fafb",
  fontSize: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  color: "#374151",
};

const COACH_PHOTO_SIZE = 84;
const avatarImg: React.CSSProperties = {
  width: COACH_PHOTO_SIZE, height: COACH_PHOTO_SIZE, borderRadius: 12, objectFit: "cover", display: "block",
  border: "1px solid #cfe8ff", background: "#fff",
};
const avatarFallback: React.CSSProperties = {
  width: COACH_PHOTO_SIZE, height: COACH_PHOTO_SIZE, borderRadius: 12, background: "#bfdbfe", color: "#1e3a8a",
  display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 22,
};
const outlineBtnStyle: React.CSSProperties = {
  padding: "8px 12px", borderRadius: 999, background: "#fff", border: "1px solid #93c5fd",
  color: "#1d4ed8", fontWeight: 700, fontSize: 14, cursor: "pointer",
};

const creditPillBase: React.CSSProperties = {
  display: "inline-block",
  padding: "6px 10px",
  borderRadius: 999,
  fontWeight: 700,
  fontSize: 12,
  border: "1px solid",
};
const creditPillOk: React.CSSProperties = {
  ...creditPillBase, color: "#065f46", background: "#d1fae5", borderColor: "#a7f3d0",
};
const creditPillAlert: React.CSSProperties = {
  ...creditPillBase, color: "#991b1b", background: "#fee2e2", borderColor: "#fecaca",
};
const cuestionarioBtn: React.CSSProperties = {
  padding: "10px",
  borderRadius: 8,
  background: "#f3f4f6",
  cursor: "pointer",
  border: "1px solid #ddd",
  fontSize: 16,
  textAlign: "left",
};
